import { format, parseISO } from "date-fns";
import { ModerationAction } from "../../api/moderation";

/**
 * Format ISO datetime string to readable format
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return "-";
  try {
    const date = parseISO(dateString);
    return format(date, "yyyy-MM-dd HH:mm:ss");
  } catch (error) {
    return dateString;
  }
};

/**
 * Get color styling for moderation action badges
 */
export const getActionColor = (action: ModerationAction) => {
  switch (action) {
    case "FLAGGED":
      return { bgcolor: "#ff9800", color: "#ffffff" };
    case "DELETED":
      return { bgcolor: "#e4463a", color: "#ffffff" };
    default:
      return { bgcolor: "#9e9e9e", color: "#ffffff" };
  }
};

/**
 * Format confidence score as percentage
 */
export const formatConfidence = (confidence: number): string => {
  return `${(confidence * 100).toFixed(1)}%`;
};

/**
 * Format categories array into readable string
 */
export const formatCategories = (categories: string[]): string => {
  if (!categories || categories.length === 0) return "-";
  return categories.join(", ");
};

/**
 * Truncate long text with ellipsis
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return "-";
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Validate threshold value (0.0 - 1.0)
 */
export const isValidThreshold = (value: number): boolean => {
  return value >= 0 && value <= 1;
};

/**
 * Validate timeout value (1000 - 30000ms)
 */
export const isValidTimeout = (value: number): boolean => {
  return value >= 1000 && value <= 30000;
};

/**
 * How long ago something happened, in words — "just now", "3h ago", "2d ago".
 *
 * For columns where the age is the point and the exact instant is not. Pair it
 * with `formatDateTime` in a tooltip: a moderator triaging a queue reads how
 * long a thing has waited, and only occasionally needs the timestamp itself.
 */
export const formatAge = (dateString: string): string => {
  if (!dateString) return "-";
  try {
    const then = parseISO(dateString).getTime();
    if (Number.isNaN(then)) return dateString;
    // Clamped at zero: a clock skewed a few seconds ahead should read as new,
    // not as a negative age.
    const minutes = Math.max(0, Math.floor((Date.now() - then) / 60000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  } catch (error) {
    return dateString;
  }
};
